import { Info, Lightbulb } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

// Detailed info for each application module
const APP_INFO = {
  dashboard: {
    title: "Dashboard",
    description: "Your central hub for monitoring tariff optimization activity across all modules. Track savings, view recent analyses, and identify opportunities at a glance.",
    features: [
      "Real-time savings potential tracking",
      "Risk and compliance alerts",
      "AI-powered insights summary",
      "Quick access to recent analyses"
    ],
    proTip: "Check the AI Insights widget to see your cumulative savings and classification accuracy."
  },
  analyzer: {
    title: "Product Analyzer",
    description: "AI-powered HS code classification engine that analyzes your product descriptions and suggests optimal tariff codes with confidence scoring.",
    features: [
      "Single product quick analysis",
      "Bulk CSV/Excel upload (up to 50,000 SKUs)",
      "Multi-model AI support (Gemini, GPT-4o, Claude)",
      "Confidence scoring and alternative suggestions"
    ],
    proTip: "Include material composition and intended use in descriptions for more accurate classifications."
  },
  sourcing: {
    title: "Smart Sourcing",
    description: "Discover alternative sourcing countries that could reduce your duty costs while maintaining product quality and supply chain efficiency.",
    features: [
      "Country-by-country duty comparison",
      "Trade agreement benefits analysis",
      "Supply chain risk assessment",
      "Cost optimization recommendations"
    ],
    proTip: "Compare at least 3-4 alternative countries to find the optimal balance of cost and reliability."
  },
  origin: {
    title: "Origin & Preference",
    description: "Determine product origin under various trade rules and identify applicable preferential trade agreements to reduce or eliminate duties.",
    features: [
      "Rules of origin determination",
      "FTA/trade agreement eligibility",
      "Substantial transformation analysis",
      "Certificate of origin guidance"
    ],
    proTip: "Products with multi-country sourcing may qualify for preferential treatment - check all applicable FTAs."
  },
  financial: {
    title: "Financial Analyser",
    description: "Calculate the financial impact of tariff changes, duty savings, and landed costs to quantify the ROI of classification optimization.",
    features: [
      "Duty savings calculations",
      "Landed cost breakdown",
      "Annual impact projections",
      "Multi-scenario comparison"
    ],
    proTip: "Input accurate annual volumes to get realistic savings projections for budget planning."
  },
  'refund-radar': {
    title: "Tariff Refund Radar",
    description: "Estimate potential refund exposure on import duties following tariff rulings or regulatory changes, such as Section 301 and Section 232 tariffs.",
    features: [
      "Quick scan for single entries",
      "CSV batch upload for bulk analysis",
      "Rule-based eligibility screening",
      "Breakdown by tariff program"
    ],
    proTip: "Upload your complete customs entry history to identify all potentially refundable duties from challenged tariffs."
  },
  governance: {
    title: "Governance & Compliance",
    description: "Monitor classification risks, compliance status, and audit readiness across your product catalog. Identify inconsistencies before they become problems.",
    features: [
      "Risk scoring and alerts",
      "Classification consistency checks",
      "Audit trail and documentation",
      "Compliance reporting"
    ],
    proTip: "Review flagged items weekly to maintain compliance and avoid costly customs audits."
  },
  history: {
    title: "Analysis History",
    description: "Access all your past classifications, reports, and exports. Track changes over time and maintain records for audit purposes.",
    features: [
      "Searchable analysis archive",
      "Export to CSV/PDF",
      "Shareable report links",
      "Version history tracking"
    ],
    proTip: "Use the share feature to collaborate with your customs broker or compliance team."
  },
  billing: {
    title: "Billing & Plans",
    description: "Manage your subscription, view credit usage, and upgrade your plan to access more features and higher analysis limits.",
    features: [
      "Credit usage tracking",
      "Plan comparison and upgrades",
      "Invoice history",
      "Payment method management"
    ],
    proTip: "Enterprise plans include API access and dedicated support - contact us for custom pricing."
  },
  settings: {
    title: "Settings",
    description: "Configure your account preferences, notification settings, and default analysis parameters.",
    features: [
      "Profile management",
      "Notification preferences",
      "Default country settings",
      "Language selection"
    ],
    proTip: "Set your default import country to speed up future analyses."
  },
  admin: {
    title: "Admin Portal",
    description: "Platform administration tools for managing users, viewing system analytics, and configuring global settings.",
    features: [
      "User management",
      "System analytics",
      "Promo code management",
      "Global AI insights"
    ],
    proTip: "Monitor the AI feedback stats to identify areas where classification accuracy can be improved."
  }
};

const AppInfo = ({ appId, className = "" }) => {
  const info = APP_INFO[appId];
  
  if (!info) return null;
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button 
          className={`p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors ${className}`}
          aria-label={`About ${info.title}`}
          data-testid={`app-info-${appId}`}
        >
          <Info className="w-5 h-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <Info className="w-4 h-4 text-[#2563EB]" />
            <h3 className="font-semibold text-slate-900">About {info.title}</h3>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">
            {info.description}
          </p>
        </div>
        
        <div className="p-4 border-b border-slate-100">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Key Features</h4>
          <ul className="space-y-1.5">
            {info.features.map((feature, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                <span className="text-[#2563EB] mt-1">•</span>
                {feature}
              </li>
            ))}
          </ul>
        </div>
        
        <div className="p-4 bg-amber-50/50">
          <div className="flex items-start gap-2">
            <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-xs font-semibold text-amber-700">Pro Tip</span>
              <p className="text-sm text-amber-800 mt-0.5">{info.proTip}</p>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AppInfo;
